import { THEMES, type ThemePreference } from "@prisma/design-tokens";
import { Card } from "@prisma/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../../components/PageHeader";
import { changeLocale, PSEUDO_LOCALE, type Locale } from "../../i18n";
import { api, call } from "../../lib/api";
import { useSession } from "../../lib/session";
import { applyTheme, getThemePreference } from "../../lib/theme";

const TIME_ZONES = ["Asia/Kuala_Lumpur", "Asia/Kuching", "Asia/Singapore", "Asia/Jakarta", "UTC"];
const SHOW_PSEUDO = import.meta.env.DEV || import.meta.env.MODE === "test";

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const me = useSession();
  const qc = useQueryClient();
  const [theme, setTheme] = useState<ThemePreference>(getThemePreference);
  const save = useMutation({
    mutationFn: (body: { locale?: "en" | "ms"; time_zone?: string }) =>
      call(api.PATCH("/app/v1/me/preferences", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
  const languages: Locale[] = SHOW_PSEUDO ? ["en", "ms", PSEUDO_LOCALE] : ["en", "ms"];
  const label = "mb-2 block text-[13px] font-medium text-ink-2";
  const control =
    "h-11 w-full max-w-sm rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[15px] text-ink";

  return (
    <div className="space-y-8">
      <PageHeader titleKey="settings.title" subtitle={t("settings.subtitle")} />
      <Card className="max-w-3xl space-y-8 p-8">
        <fieldset>
          <legend className={label}>{t("settings.appearance")}</legend>
          <div className="flex gap-2" role="radiogroup">
            {THEMES.map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={theme === v}
                onClick={() => {
                  applyTheme(v);
                  setTheme(v);
                }}
                className="h-10 rounded-[var(--radius-control)] border border-line px-4 text-[14px] text-ink aria-checked:border-[var(--accent-primary)] aria-checked:bg-[var(--accent-primary-soft)]"
              >
                {t(`shell.theme_${v}`)}
              </button>
            ))}
          </div>
        </fieldset>
        <div>
          <label htmlFor="lang" className={label}>
            {t("settings.language")}
          </label>
          <select
            id="lang"
            className={control}
            value={i18n.language}
            onChange={(e) => {
              const v = e.target.value as Locale;
              void changeLocale(v);
              if (v !== PSEUDO_LOCALE) save.mutate({ locale: v });
            }}
          >
            {languages.map((l) => (
              <option key={l} value={l}>
                {t(`settings.lang_${l}`)}
              </option>
            ))}
          </select>
          <p className="mt-2 text-[13px] text-ink-3">{t("settings.language_hint")}</p>
        </div>
        <div>
          <label htmlFor="tz" className={label}>
            {t("settings.time_zone")}
          </label>
          <select
            id="tz"
            className={control}
            value={me.time_zone}
            onChange={(e) => save.mutate({ time_zone: e.target.value })}
          >
            {TIME_ZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>
        {save.isSuccess && (
          <p role="status" className="text-[13px] text-[var(--status-success)]">
            {t("settings.saved")}
          </p>
        )}
      </Card>
    </div>
  );
}
